/**
 * Темы разговоров (#845 PR 6, спека docs/plans/2026-10-02-conversation-themes.md
 * §5) во вкладках «Продажи» и «Сайт и контент» — строки блока без React.
 *
 * Строки — те же `VoiceRow`, что у остальных блоков раздела: одна карточка, одно
 * раскрытие до разговоров. Своё у темы лежит в `VoiceRow.theme` (определение,
 * категории, качество, отказы, вопросы без ответа) и рисуется раскрытием.
 *
 * Знаменатель строки — её собственный (`callsInWindow` строки: разговоры, где
 * тема была в наборе), а не охват блока. Сдвиг к прошлому окну есть, только
 * когда сервер прислал прошлые числа строки (`prevCalls !== null`): иначе
 * вместо стрелки — подпись «с <даты>» или «определение изменено <дата>», а
 * если окна прочитаны разными ревизиями промпта тем (#2952), — что способ
 * присвоения тем обновлён: дата базы тогда ни при чём.
 */
import type {
  CallCategory,
  OrgTheme,
  VoiceThemeRow,
  VoiceThemes,
  VoiceThemesOnlyResponse,
  VoiceThemePrevIncomparableReason,
  VoiceThemeSinceReason,
} from '@aica/api-types/schemas';
import { UNCATEGORIZED_THEME_CODE } from '@aica/api-types/schemas';
import { percent } from '@/components/analytics/objections/objection-view-model';
import {
  isSmallSample,
  type VoiceRow,
  type VoiceRowItem,
  type VoiceSection,
} from './voice-view-model';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Состояние блока в ответе — как у блоков вопросов: блок пришёл; флаг открыт, а
 * чтение упало (`failed`, экран предлагает повтор); ключа нет и сбоя нет —
 * шлюз закрыт, ничего не рисуется.
 */
export type VoiceThemesState =
  { kind: 'ready'; themes: VoiceThemes } | { kind: 'failed' } | { kind: 'closed' };

export function themesState(
  data: Pick<VoiceThemesOnlyResponse, 'themes' | 'themesUnavailable'>
): VoiceThemesState {
  if (data.themes) return { kind: 'ready', themes: data.themes };
  return data.themesUnavailable === true ? { kind: 'failed' } : { kind: 'closed' };
}

/**
 * В наборе нет активных тем (#2072, спека 2026-10-11-voice-report-layers.md
 * §2.6): строки блока — все активные темы набора, с нулями, поэтому пустые
 * строки и есть пустой набор. Тогда нет ни раздела «Темы», ни карточки темы — в
 * шапке одна кнопка «Задать темы».
 */
export function themeSetEmpty(themes: VoiceThemes): boolean {
  return themes.rows.length === 0;
}

/**
 * В списке набора есть активная тема (зарезервированный код — ещё не тема).
 * Тот же список читает кнопка в шапке, и он не зависит от периода: по нему
 * вкладки решают, есть ли раздел «Темы», не дожидаясь аналитики окна (#2087).
 */
export function themeListHasActive(themes: readonly Pick<OrgTheme, 'status'>[]): boolean {
  return themes.some((theme) => theme.status === 'active');
}

/**
 * В ответе аналитики есть строки демо-набора (#2008, `demo` строки):
 * демо-организация видит темы демо-разговоров, хотя её собственный набор — тот,
 * что в списке, — пуст. Вкладкам это тоже «набор есть»: иначе демо-темы не
 * показались бы нигде.
 */
export function themesHaveDemoRows(themes: VoiceThemes): boolean {
  return themes.rows.some((row) => row.demo === true);
}

/**
 * Пуст ли набор для раздела «Темы» вкладки; `undefined` — ещё неизвестно
 * (раздела нет). Решает список набора — тот же, что у кнопки в шапке; список
 * не прочитался — строки аналитики. Демо-набора (#2008) в списке нет, поэтому
 * при показанном демо решает ответ аналитики: строки демо-набора — набор есть;
 * ответа ещё нет или он упал — раздел есть, с загрузкой или с ошибкой и
 * повтором (сбой шага тем не прячется), как у клиента со своим набором.
 */
export function themeSectionSetEmpty(input: {
  /** Ответ аналитики окна, если он пришёл и не упал. */
  ready: VoiceThemes | undefined;
  /** Запрос показывает демо (`includeDemo=yes`). */
  demoShown: boolean;
  /** Список набора: прочитан — его темы, упал — `failed`, читается — `undefined`. */
  list: readonly Pick<OrgTheme, 'status'>[] | 'failed' | undefined;
}): boolean | undefined {
  const { ready, demoShown, list } = input;
  if (ready ? themesHaveDemoRows(ready) : demoShown) return false;
  if (list === 'failed') return Boolean(ready && themeSetEmpty(ready));
  return list ? !themeListHasActive(list) : undefined;
}

/**
 * Набор есть, а в окне нет разговоров ни с одной темой: раздел остаётся со
 * строкой охвата (сбой шага тем не прячется), карточки темы нет.
 */
export function themesWithoutCalls(themes: VoiceThemes): boolean {
  return themes.rows.every((row) => row.calls === 0);
}

/**
 * Срез «вопросы без ответа» в ответе есть: сервер опускает его у читателя без
 * права на текст разговоров (`evidence.text`/`records.read`) — тогда у «Сайта»
 * нет карточки темы, а не «мало данных» (раздел «Темы» остаётся).
 */
export function themesHaveUnanswered(themes: VoiceThemes): boolean {
  return themes.rows.some((row) => row.unanswered !== undefined);
}

/**
 * Почему прошлое окно строке не сравнимо: с какой даты она считается или
 * (#2952) окна прочитаны разными ревизиями промпта тем — тогда даты нет.
 */
export type VoiceThemeBaseline =
  | { reason: VoiceThemeSinceReason; since: string }
  | { reason: VoiceThemePrevIncomparableReason };

/** Что раскрытие темы показывает сверх списка разговоров. */
export interface VoiceThemeExtra {
  /** Пусто — определения нет, блок не рисуется. */
  definition: string;
  /** `null` — прошлое окно сравнимо, сдвиг показывает карточка. */
  baseline: VoiceThemeBaseline | null;
  /** Как отдал сервер: только категории с разговорами, больше разговоров выше. */
  categories: { category: CallCategory; calls: number }[];
  /** «Продажи»: средний балл темы против среднего по всем разговорам с темами. */
  quality?: {
    scoredCalls: number;
    /** Целый балл 0–100; `null` — ни у одного разговора темы балла нет. */
    score: number | null;
    /** Среднее по всем разговорам с темами; `null` — его в ответе нет. */
    overallScore: number | null;
    smallSample: boolean;
  };
  /** «Продажи»: отказы среди разговоров продаж темы. */
  declines?: {
    salesCalls: number;
    declinedCalls: number;
    /** `null` — разговоров продаж нет: доли нет, а не «0 %». */
    sharePercent: number | null;
  };
  /** «Сайт и контент»: разговоры темы, где вопрос остался без полного ответа. */
  unanswered?: { calls: number; items: VoiceRowItem[] };
}

/** Категория разговора — деталь разговора в списке: `itemDetail` переводит её. */
const toItem = (item: {
  recordId: string;
  createdAt: string;
  text: string;
  callCategory: CallCategory | null;
}): VoiceRowItem => ({
  recordId: item.recordId,
  createdAt: item.createdAt,
  text: item.text,
  detail: item.callCategory,
});

function themeExtra(
  row: VoiceThemeRow,
  themes: VoiceThemes,
  comparable: boolean
): VoiceThemeExtra {
  return {
    definition: row.definition,
    baseline: comparable
      ? null
      : row.prevIncomparable
        ? { reason: row.prevIncomparable }
        : { reason: row.sinceReason, since: row.since },
    categories: row.categories,
    ...(row.quality
      ? {
          quality: {
            scoredCalls: row.quality.scoredCalls,
            // Целый балл приходит от сервера: второго округления на клиенте нет.
            score: row.quality.averageScore,
            overallScore: themes.quality?.averageScore ?? null,
            smallSample: isSmallSample(row.quality.scoredCalls),
          },
        }
      : {}),
    ...(row.declines
      ? {
          declines: {
            salesCalls: row.declines.salesCalls,
            declinedCalls: row.declines.declinedCalls,
            sharePercent:
              row.declines.salesCalls > 0
                ? percent(row.declines.declinedCalls, row.declines.salesCalls)
                : null,
          },
        }
      : {}),
    ...(row.unanswered
      ? {
          unanswered: {
            calls: row.unanswered.calls,
            items: row.unanswered.items.map(toItem),
          },
        }
      : {}),
  };
}

/**
 * Строка темы: «в K из N разговоров» по N самой строки. Тема, с чьей базы
 * разговоров ещё не было (`callsInWindow === 0`), говорит об этом словами, а не
 * «в 0 из 0 · 0 %».
 */
function themeRow(row: VoiceThemeRow, themes: VoiceThemes): VoiceRow {
  // The server sends the previous numbers only for a comparable base: the
  // card's baseline and the table's empty cells read this one verdict.
  const comparable = row.prevCalls !== null && row.prevCallsInWindow !== null;
  return {
    id: `theme:${row.code}`,
    kind: 'theme',
    key: row.name,
    seriesKey: row.code,
    calls: row.calls,
    // No previous numbers (`null`): the trend stays silent, the card names the base.
    prevCalls: row.prevCalls ?? 0,
    ...(row.mark ? { mark: row.mark } : {}),
    callsInWindow: row.callsInWindow,
    prevCallsInWindow: row.prevCallsInWindow ?? 0,
    ...(comparable ? {} : { noPrevious: true as const }),
    ...(row.callsInWindow === 0
      ? { countsKey: 'voicePage.themes.analytics.noCallsYet' }
      : {}),
    items: row.items.map(toItem),
    theme: themeExtra(row, themes, comparable),
  };
}

/**
 * «Без темы»: шаг ответил, что ни одна тема набора не подходит. Строка — шапка
 * блока и видна всегда: её доля — мера годности набора (§4). N — разговоры, где
 * темы присвоены.
 */
export function noThemeRow(themes: VoiceThemes): VoiceRow {
  // The previous share of «no theme» depends on the theme set. Whether the set
  // was the same in the previous window is the server's verdict
  // (`uncategorized.prevCalls === null` — it saw every theme, the archived ones
  // too): the client derives nothing from the active rows, which miss an
  // archived theme and are vacuously «comparable» for an empty set.
  const comparable = themes.uncategorized.prevCalls !== null;
  return {
    id: 'noTheme:uncategorized',
    kind: 'noTheme',
    key: 'uncategorized',
    seriesKey: UNCATEGORIZED_THEME_CODE,
    calls: themes.uncategorized.calls,
    prevCalls: themes.uncategorized.prevCalls ?? 0,
    ...(themes.uncategorized.mark ? { mark: themes.uncategorized.mark } : {}),
    callsInWindow: themes.coverage.assignedCalls,
    prevCallsInWindow: comparable ? themes.coverage.prevAssignedCalls : 0,
    ...(comparable ? {} : { noPrevious: true as const }),
    items: themes.uncategorized.items.map(toItem),
  };
}

/**
 * Темы набора — в порядке сервера (больше разговоров выше, с нулями). N блока —
 * разговоры с присвоенными темами: 0 — «не собрано» словами, а не «0 из 0».
 */
export function themeSection(themes: VoiceThemes): VoiceSection {
  return {
    id: 'themes',
    callsInWindow: themes.coverage.assignedCalls,
    // Пустой набор блока не рисует (#2072): строки есть всегда.
    zeroKey: 'voicePage.themes.analytics.notAssigned',
    rows: themes.rows.map((row) => themeRow(row, themes)),
  };
}

/**
 * Блок тем в выгрузке вкладки (#2009): «Без темы» первой — на экране она шапка
 * блока, — затем темы набора. Ответ тем — свой запрос со своим потолком
 * (`cap`): оговорка вопросов за него не говорит.
 */
export function themeExportSection(themes: VoiceThemes): VoiceSection {
  const section = themeSection(themes);
  return {
    ...section,
    rows: [noThemeRow(themes), ...section.rows],
    baseKey: 'voicePage.export.base.themes',
    // N of the block itself — said by the one line of a block with N = 0.
    blockBaseKey: 'voicePage.export.base.themesAssigned',
    cap: {
      truncated: themes.truncated,
      prevTruncated: themes.prevTruncated,
      truncatedKey: 'voicePage.truncated',
      prevTruncatedKey: 'voicePage.prevTruncated',
    },
  };
}

/**
 * Строки печатной формы о блоке тем — те же, что экран пишет над и под ним
 * (`VoiceThemesHeader`, `VoiceThemesBlock`): охват — когда темы присвоены или
 * шаг не смог их определить, пояснение «Без темы» и N — когда присвоены.
 */
export function themePrintNotes(themes: VoiceThemes, t: Translate): string[] {
  const { assignedCalls, degradedCalls } = themes.coverage;
  return [
    ...(assignedCalls > 0 || degradedCalls > 0 ? themeCoverageLines(themes, t) : []),
    ...(assignedCalls > 0
      ? [
          t('voicePage.themes.analytics.noThemeHint'),
          t('voicePage.themes.analytics.denominatorNote'),
        ]
      : []),
  ];
}

/** Строки охвата над блоком: знаменатель называется до числителей. */
export function themeCoverageLines(themes: VoiceThemes, t: Translate): string[] {
  const { coverage } = themes;
  return [
    t('voicePage.themes.analytics.coverage', {
      assigned: coverage.assignedCalls,
      count: coverage.analyzedCalls,
    }),
    ...(coverage.degradedCalls > 0
      ? [t('voicePage.themes.analytics.degraded', { count: coverage.degradedCalls })]
      : []),
  ];
}

/** Подпись строки, у которой прошлое окно не сравнимо. */
export function themeBaselineText(
  baseline: VoiceThemeBaseline,
  t: Translate,
  formatDate: (iso: string) => string
): string {
  if (baseline.reason === 'prompt_revision') {
    return t('voicePage.themes.analytics.baseline.promptRevision');
  }
  return t(
    baseline.reason === 'definition_changed'
      ? 'voicePage.themes.analytics.baseline.definitionChanged'
      : 'voicePage.themes.analytics.baseline.since',
    { date: formatDate(baseline.since) }
  );
}

/** Одна категория звонка и её темы. */
export interface CategoryThemes {
  category: CallCategory;
  /** Сумма по темам: разговор с несколькими темами в ней — несколько раз. */
  calls: number;
  themes: { code: string; name: string; calls: number }[];
}

/**
 * «У категории — её темы» (§5): обращение «тема → категории» строк. Категории,
 * где нет разговоров с темами, не попадают; больше разговоров выше, при равенстве
 * — порядок строк сервера.
 */
export function categoryThemes(rows: readonly VoiceThemeRow[]): CategoryThemes[] {
  const byCategory = new Map<CallCategory, CategoryThemes>();
  for (const row of rows) {
    for (const entry of row.categories) {
      if (entry.calls <= 0) continue;
      const slot = byCategory.get(entry.category) ?? {
        category: entry.category,
        calls: 0,
        themes: [],
      };
      slot.calls += entry.calls;
      slot.themes.push({ code: row.code, name: row.name, calls: entry.calls });
      byCategory.set(entry.category, slot);
    }
  }
  return [...byCategory.values()]
    .map((slot) => ({
      ...slot,
      themes: [...slot.themes].sort((a, b) => b.calls - a.calls),
    }))
    .sort((a, b) => b.calls - a.calls);
}

/**
 * Блок тем для вида «Что изменилось» (#2394 §9 п. 7): строки набора и
 * «Без темы» — у неё тоже своя пометка, а в блоке она стоит шапкой.
 */
export function themeChangeSection(themes: VoiceThemes): VoiceSection {
  const section = themeSection(themes);
  return { ...section, rows: [...section.rows, noThemeRow(themes)] };
}
